/**
 * Mahnschreiben als PDF (Befund 13, Umfang wie das Mahnschreiben des bisherigen Portals): datiertes Schreiben der Agentur mit
 * Forderungsaufstellung (Hauptforderung, bereits gezahlt, Mahngebühr, Verzugszinsen mit Satz, Zeitraum und Rechtsgrundlage),
 * neuer Frist und Bankverbindung. Dieselben Zahlen wie in der Vorschau (computeDunningCosts) und in der Mail.
 * Nutzt den PDF-Schreiber der Rechnung (invoice-pdf.ts); läuft im Browser, in Deno und in Node.
 */
import { isSwissGerman, swissDeep } from "./swiss-spelling.js";
import { normalizeCountry } from "./countries.js";
import { consumerInterestRule } from "./dunning.js";
import type { BankDetails } from "./document-model.js";
import type { DunningCosts } from "./dunning.js";
import { PDF_LAYOUT, MUTED, Page, QR_SLIP_HEIGHT, assemble, belegFarben, belegGirocode, belegLogo, drawSwissQrSlip, fmtDate, footerWrapped, groupIban, pagesToSvg, pdfAccent, textWidth, wrapText, type InvoicePdfParty, type PdfImage } from "./invoice-pdf.js";
import { BELEG_SCHRIFT_FAKTOR, belegLayout, girocodeInhalt } from "./beleg-layout.js";
import { qrText, type SwissQrBill } from "./swiss-qr-bill.js";
import { formatMoney } from "./money.js";
import type { Currency, IsoDate, Language, MoneyLocale } from "./types.js";

export interface DunningLetterModel {
  language: Language;
  locale: MoneyLocale;
  currency: Currency;
  sender: InvoicePdfParty;
  /**
   * Probelauf N4: Mahnt die Agentur eine Forderung des Creators (Rechnung „Creator mit Vollmacht an Marke“), ist sie der
   * Absender (Briefkopf, Unterschrift) und schreibt „im Auftrag von“ dem Creator; sender ist dann die Agentur, hier der Creator.
   */
  onBehalfOf?: string | null;
  /** Fußzeile der Agentur (Firma, Vertretung, Register) */
  footerLines: string[];
  recipient: InvoicePdfParty;
  /** Datum des Schreibens */
  date: IsoDate;
  /** „Zahlungserinnerung“, „1. Mahnung“ … */
  title: string;
  /**
   * K-404: Farbe der Überschrift nach Stufentyp (nicht nach dem Namen, gilt also auch für eigene Stufen und Englisch):
   * Zahlungserinnerung dunkles Gold, jede Mahnung gedecktes Rot; ohne Angabe die Akzentfarbe der Agentur.
   */
  tone?: "reminder" | "notice" | null;
  invoice: { number: string; date: IsoDate | null; dueDate: IsoDate | null; grossCents: number };
  /** Text des Schreibens (Absätze), ohne HTML */
  paragraphs: string[];
  costs: Pick<DunningCosts, "principalCents" | "feeCents" | "interestCents" | "totalCents" | "interest" | "feeBasis">;
  /** bereits eingegangener Teil der Rechnung */
  paidCents?: number;
  deadline: IsoDate;
  bank: BankDetails | null;
  /** Rechtsgrundlage der Zinsen (z. B. „§ 288 Abs. 2 BGB“) */
  interestBasis?: string | null;
  accent?: string | null;
  logo?: PdfImage | null;
  /** Fußzeile für Mails und Mahnschreiben (org_settings.email_footer, SAAS-R3-9): steht unter dem Schreiben */
  closingLines?: string[] | null;
  /** PDF-Vorlage der Agentur für Mahnschreiben (beleg-layout.ts, 0456); ohne Angabe wie bisher */
  layout?: unknown;
  /**
   * Schweizer QR-Zahlteil (PRAXIS-R3-6): Zahlteil der Rechnung mit dem offenen Gesamtbetrag der Forderungsaufstellung
   * (Rest, Gebühr, Zinsen) und derselben Referenz; ohne Zahlteil der Rechnung keiner.
   */
  qrBill?: SwissQrBill | null;
}

/** Zahlteil des Mahnschreibens aus dem Zahlteil der Rechnung: Betrag = offener Gesamtbetrag, Mitteilung mit dem Titel */
export function dunningQrBill(invoiceBill: SwissQrBill | null | undefined, totalCents: number, title: string, number: string): SwissQrBill | null {
  if (!invoiceBill || !(totalCents > 0)) return null;
  const lang = invoiceBill.language === "en" ? "en" : "de";
  return { ...invoiceBill, amountCents: totalCents, message: qrText(`${title} ${lang === "en" ? "invoice" : "Rechnung"} ${number}`, 140) };
}

/** Rechtsgrundlage der Verzugszinsen nach Land der Agentur; Geschäfte zwischen Unternehmen, mit `consumer` gegenüber Privatpersonen (SAAS-3) */
export function interestBasisFor(country: string | null | undefined, consumer = false): string | null {
  if (consumer) return consumerInterestRule(country)?.basis ?? null;
  switch (normalizeCountry(country ?? null)) {
    case "DE": return "§ 288 Abs. 2 BGB";
    case "AT": return "§ 456 UGB";
    case "CH": return "Art. 104 OR";
    default: return null;
  }
}

/**
 * K-420: Rechtsgrundlage im englischen Schreiben in englischer Zitierweise: „§ 288 Abs. 2 BGB“ → „section 288 (2) BGB“,
 * „§ 456 UGB“ → „section 456 UGB“, „Art. 104 Abs. 1 OR“ → „Art. 104 para. 1 OR“. Unbekanntes bleibt unverändert.
 */
export function legalRefEn(basis: string | null | undefined): string | null {
  if (!basis) return null;
  return basis
    .replace(/§\s*(\d+[a-z]?)\s+Abs\.\s*(\d+)/g, "section $1 ($2)")
    .replace(/§\s*(\d+[a-z]?)/g, "section $1")
    .replace(/Art\.\s*(\d+[a-z]?)\s+Abs\.\s*(\d+)/g, "Art. $1 para. $2")
    .replace(/\bi\.\s?V\.\s?m\.\s/g, "in conjunction with ");
}

/** Rechtsgrundlage als ein Block: geschützte Leerzeichen, damit „§ 288 Abs. 2 BGB“ nicht über zwei Zeilen läuft */
export function keepTogether(basis: string | null | undefined): string | null {
  return basis ? basis.replace(/ /g, " ") : null;
}

const T = {
  de: {
    subject: (title: string, n: string) => `${title} zur Rechnung ${n}`, date: "Datum", claim: "Forderungsaufstellung",
    principal: (n: string, d: string | null, due: string | null) => `Rechnung ${n}${d ? ` vom ${d}` : ""}${due ? `, fällig am ${due}` : ""}`,
    paid: "abzüglich bereits gezahlt", fee: "Mahngebühr", interest: (rate: string, from: string, to: string, days: number, basis: string | null) =>
      `Verzugszinsen ${rate} p. a. vom ${from} bis ${to} (${days} ${days === 1 ? "Tag" : "Tage"})${basis ? ` gem. ${basis}` : ""}`,
    total: "Offener Gesamtbetrag", pay: (amount: string, deadline: string, n: string) =>
      `Bitte überweisen Sie ${amount} bis spätestens ${deadline} unter Angabe der Rechnungsnummer ${n}.`,
    payQr: (amount: string, deadline: string) =>
      `Bitte zahlen Sie ${amount} bis spätestens ${deadline} mit dem Zahlteil unten; er enthält den offenen Gesamtbetrag. Der Zahlteil der Rechnung gilt nicht mehr.`,
    bank: "Bankverbindung", holder: "Kontoinhaber", iban: "IBAN", bic: "BIC", bankName: "Bank", page: (a: number, b: number) => `Seite ${a} von ${b}`,
  },
  en: {
    subject: (title: string, n: string) => `${title} for invoice ${n}`, date: "Date", claim: "Statement of claim",
    principal: (n: string, d: string | null, due: string | null) => `Invoice ${n}${d ? ` dated ${d}` : ""}${due ? `, due on ${due}` : ""}`,
    paid: "less amount already paid", fee: "Dunning fee", interest: (rate: string, from: string, to: string, days: number, basis: string | null) =>
      `Default interest ${rate} p.a. from ${from} to ${to} (${days} ${days === 1 ? "day" : "days"})${basis ? ` under ${basis}` : ""}`,
    total: "Total amount outstanding", pay: (amount: string, deadline: string, n: string) =>
      `Please transfer ${amount} by ${deadline} at the latest, quoting invoice number ${n}.`,
    payQr: (amount: string, deadline: string) =>
      `Please pay ${amount} by ${deadline} at the latest using the payment part below; it contains the total amount outstanding. The payment part of the invoice no longer applies.`,
    bank: "Bank details", holder: "Account holder", iban: "IBAN", bic: "BIC", bankName: "Bank", page: (a: number, b: number) => `Page ${a} of ${b}`,
  },
} as const;

/** K-404: Überschriftfarben der Mahnschreiben, druckfest und mit mindestens 4,5:1 Kontrast auf Weiß (5,9:1 bzw. 7,4:1) */
export const DUNNING_TONE_COLORS = { reminder: "#8A5A00", notice: "#A1262B" } as const;

export function renderDunningLetterPdf(m: DunningLetterModel, opts: { now?: Date } = {}): Uint8Array {
  const l = layoutDunningLetter(m);
  return assemble(l.pages, l.title, opts.now ?? new Date(), l.images);
}

/**
 * K-410: Mahnschreiben als Bild je Seite (SVG) für die Vorschau im Versandfenster. Dasselbe Layout und dieselben Zeichenbefehle
 * wie renderDunningLetterPdf, also inhaltsgleich mit dem PDF im Anhang der Mail.
 */
export function renderDunningLetterSvg(m: DunningLetterModel): string[] {
  const l = layoutDunningLetter(m);
  return pagesToSvg(l.pages, l.images);
}

function layoutDunningLetter(m0: DunningLetterModel): { pages: Page[]; title: string; images: PdfImage[] } {
  const { W, H, L, R, FOOTER_TOP } = PDF_LAYOUT;
  void W;
  const lang: Language = m0.language === "en" ? "en" : "de";
  // K-452: Mahnschreiben von Agenturen in der Schweiz und Liechtenstein ohne „ß“ (QR-Zahlteil und Logo unverändert)
  const swiss = lang === "de" && isSwissGerman(m0.locale);
  const m = swiss ? swissDeep(m0, ["qrBill", "logo"]) : m0;
  const t = swiss ? swissDeep(T[lang]) : T[lang];
  // PDF-Vorlage (0456): Design, Farbe, Logo, Kopfadresse, Schriftgröße, Bankblock, Girocode, Fuß
  const lay = belegLayout(m0.layout);
  const farben = belegFarben(lay, m.accent);
  const accent = farben.titel;
  const linie = farben.linie;
  const k = BELEG_SCHRIFT_FAKTOR[lay.schrift];
  const fs = (v: number) => Math.round(v * k * 100) / 100;
  const money = (c: number) => formatMoney(c, m.currency, m.locale);
  const d = (v: string | null | undefined) => fmtDate(v ?? null, lang, m.locale);
  const pages: Page[] = [];
  let page = new Page();
  pages.push(page);
  let y = H - 56;
  // K-423: Fußzeilen umbrechen statt abschneiden; ab der fünften Zeile wächst der Fuß nach oben
  const footer = footerWrapped([...m.footerLines, ...(lay.fusstext ? [lay.fusstext] : [])], R - L - 70);
  const footTop = FOOTER_TOP + Math.max(0, footer.length - 4) * 10;
  const bottom = footTop + 12 + Math.min(4, footer.length) * 10;
  const ensure = (need: number) => {
    if (y - need < bottom) { page = new Page(); pages.push(page); y = H - 56; }
  };

  const gezeichnet = belegLogo(page, lay, m.logo, y);
  const logo = gezeichnet.logo;
  y = gezeichnet.y;
  for (const part of wrapText(m.sender.name || "–", "F2", fs(15), R - L)) { page.text(L, y, part, "F2", fs(15)); y -= fs(18); }
  const senderLine = [...(lay.kopfAdresse ? m.sender.addressLines : []), ...(m.sender.fields ?? []).map((f) => `${f.label}: ${f.value}`)].filter(Boolean).join(" · ");
  for (const part of wrapText(senderLine, "F1", fs(9), R - L)) { page.text(L, y, part, "F1", fs(9), MUTED); y -= fs(12); }
  const behalf = (m.onBehalfOf ?? "").trim();
  if (behalf) for (const part of wrapText(`${lang === "en" ? "On behalf of" : "Im Auftrag von"} ${behalf}`, "F1", 9, R - L)) { page.text(L, y, part, "F1", 9, MUTED); y -= 12; }
  y -= 24;

  // Empfänger links, Datum rechts
  const top = y;
  // K-423: Absenderzeile über dem Empfänger vollständig (umgebrochen statt abgeschnitten)
  const ruecksende = wrapText([m.sender.name, ...m.sender.addressLines].join(" · "), "F1", 7, 250);
  ruecksende.forEach((part, i) => page.text(L, y - i * 9, part, "F1", 7, MUTED));
  y -= 18 + Math.max(0, ruecksende.length - 1) * 9;
  for (const part of wrapText(m.recipient.name || "–", "F2", 11, 250)) { page.text(L, y, part, "F2", 11); y -= 14; }
  for (const a of m.recipient.addressLines) for (const part of wrapText(a, "F1", 11, 250)) { page.text(L, y, part, "F1", 11); y -= 14; }
  page.text(340, top, t.date, "F1", 9, MUTED);
  page.right(R, top, d(m.date) ?? "", "F1", 10);
  y -= 28;

  page.text(L, y, t.subject(m.title, m.invoice.number), "F2", fs(13), m.tone && lay.design !== "minimal" ? pdfAccent(DUNNING_TONE_COLORS[m.tone]) : accent);
  y -= 24;
  // Gruß und Unterschrift aus dem Mailtext stehen am Ende des Briefs, nicht über der Forderungsaufstellung (Probelauf 07.10.2026)
  const { body: bodyParas, signoff } = splitSignoff(m.paragraphs);
  for (const para of bodyParas) {
    const lines = wrapText(para, "F1", fs(10), R - L);
    ensure(lines.length * fs(13) + 8);
    for (const line of lines) { page.text(L, y, line, "F1", fs(10)); y -= fs(13); }
    y -= 8;
  }

  // Forderungsaufstellung
  y -= 6;
  ensure(8 * 16);
  if (farben.fuellung) page.rect(L - 6, y - 7, R - L + 12, 19, farben.fuellung);
  page.text(L, y, t.claim, "F2", fs(10));
  y -= 6;
  if (!farben.fuellung) page.line(L, y, R, y, 0.8, linie);
  y -= 15;
  const row = (label: string, cents: number, bold = false) => {
    const lines = wrapText(label, bold ? "F2" : "F1", 10, R - L - 120);
    ensure(lines.length * 13 + 6);
    lines.forEach((l, i) => page.text(L, y - i * 13, l, bold ? "F2" : "F1", 10));
    page.right(R, y, money(cents), bold ? "F2" : "F1", 10);
    y -= lines.length * 13 + 4;
  };
  const paid = m.paidCents ?? 0;
  row(t.principal(m.invoice.number, d(m.invoice.date), d(m.invoice.dueDate)), paid > 0 ? m.invoice.grossCents : m.costs.principalCents);
  if (paid > 0) row(t.paid, -paid);
  if (m.costs.feeCents > 0) row(feeLabel(m.costs.feeBasis, lang, t.fee), m.costs.feeCents);
  const i = m.costs.interest;
  if (i && m.costs.interestCents > 0 && i.segments.length) {
    // Eine Zeile je Zinsabschnitt (Basiszinswechsel im Zeitraum), damit die Aufstellung nachrechenbar ist. Die Zinsen werden
    // nach Satz × Tage auf die Abschnitte verteilt (kumuliert gerundet), die Summe bleibt genau der berechnete Betrag.
    const fmtRate = (r: number) => `${new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: 2 }).format(r)} %`;
    const weights = i.segments.map((s) => BigInt(Math.round(s.ratePercent * 10_000)) * BigInt(s.days));
    const totalWeight = weights.reduce((a, w) => a + w, 0n);
    const total = BigInt(m.costs.interestCents);
    let cumWeight = 0n;
    let assigned = 0n;
    i.segments.forEach((s, n) => {
      cumWeight += weights[n]!;
      const cum = n === i.segments.length - 1 || totalWeight === 0n ? total : (total * cumWeight * 2n + totalWeight) / (totalWeight * 2n);
      const cents = Number(cum - assigned);
      assigned = cum;
      // Rechtsgrundlage nie umbrechen (Probelauf Geldfluss 07.10.2026: „gem. §“ stand am Zeilenende, „288 Abs. 2 BGB“ darunter)
      row(t.interest(fmtRate(s.ratePercent), d(s.from) ?? "", d(s.to) ?? "", s.days, n === 0 ? keepTogether(lang === "en" ? legalRefEn(m.interestBasis) : m.interestBasis) : null), cents);
    });
  }
  page.line(R - 200, y + 6, R, y + 6, 0.8, linie);
  y -= 6;
  row(t.total, m.costs.totalCents, true);
  y -= 10;
  const qrBill = m.qrBill ?? null;
  const pay = wrapText(qrBill ? t.payQr(money(m.costs.totalCents), d(m.deadline) ?? "") : t.pay(money(m.costs.totalCents), d(m.deadline) ?? "", m.invoice.number), "F1", 10, R - L);
  ensure(pay.length * 13 + 10);
  for (const line of pay) { page.text(L, y, line, "F1", 10); y -= 13; }
  y -= 10;

  const bank = m.bank;
  const rows: [string, string][] = [];
  if (bank?.accountHolder?.trim()) rows.push([t.holder, bank.accountHolder.trim()]);
  if (bank?.iban?.trim()) rows.push([t.iban, groupIban(bank.iban)]);
  if (bank?.bic?.trim()) rows.push([t.bic, bank.bic.replace(/\s+/g, "").toUpperCase()]);
  if (bank?.bankName?.trim()) rows.push([t.bankName, bank.bankName.trim()]);
  const giro = lay.girocode && !qrBill && bank?.iban
    ? girocodeInhalt({ name: bank.accountHolder?.trim() || m.sender.name, iban: bank.iban, bic: bank.bic, cents: m.costs.totalCents, currency: m.currency, reference: m.invoice.number })
    : null;
  // Prüf-Chat P7: das Mahnschreiben nennt das Konto immer (Zahlungsaufforderung); die Vorlage regelt hier nur den Girocode
  if (rows.length) {
    ensure(Math.max((rows.length + 1) * 13, giro ? 96 : 0));
    const start = y;
    if (giro) belegGirocode(page, giro, y, 78, lang);
    page.text(L, y, t.bank, "F2", 9.5);
    y -= 13;
    for (const [label, value] of rows) { page.text(L, y, label, "F1", 9.5, MUTED); page.text(L + 90, y, value, "F1", 9.5); y -= 13; }
    if (giro) y = Math.min(y, start - 96);
  } else if (giro) { ensure(96); belegGirocode(page, giro, y, 78, lang); y -= 96; }

  if (signoff.length) {
    y -= 10;
    for (const para of signoff) {
      const lines = wrapText(para, "F1", 10, R - L);
      ensure(lines.length * 13 + 6);
      for (const line of lines) { page.text(L, y, line, "F1", 10); y -= 13; }
      y -= 4;
    }
  }

  // Fußzeile für Mails und Mahnschreiben aus dem Branding (SAAS-R3-9), wie unter der Mail
  const closing = (m.closingLines ?? []).map((x) => x.trim()).filter(Boolean);
  if (closing.length) {
    y -= 8;
    for (const c of closing) {
      const lines = wrapText(c, "F1", 9, R - L);
      ensure(lines.length * 12);
      for (const line of lines) { page.text(L, y, line, "F1", 9, MUTED); y -= 12; }
    }
  }

  // Schweizer QR-Zahlteil über den offenen Betrag (PRAXIS-R3-6): unten auf der letzten Seite, sonst auf einer eigenen Seite
  const slipFooterShift = QR_SLIP_HEIGHT + 13 - (footTop - 12 - 10 * Math.max(0, footer.length - 1));
  let slipPage: Page | null = null;
  if (qrBill) {
    if (y + 19 < footTop + slipFooterShift + 12) { page = new Page(); pages.push(page); y = H - 56; }
    slipPage = page;
    drawSwissQrSlip(page, qrBill);
  }

  pages.forEach((p, n) => {
    const top = footTop + (p === slipPage ? slipFooterShift : 0);
    p.line(L, top, R, top, 0.4);
    let fy = top - 12;
    for (const f of footer) { p.text(L, fy, f, "F1", 7.5, MUTED); fy -= 10; }
    if (lay.seitenzahlen) p.right(R, top - 12, t.page(n + 1, pages.length), "F1", 7.5, MUTED);
    if (lay.design === "akzent") p.rect(0, H - 10, PDF_LAYOUT.W, 10, farben.marke);
  });
  void textWidth;
  return { pages, title: t.subject(m.title, m.invoice.number), images: logo ? [logo] : [] };
}

/** Mailtext (HTML) in Absätze für das Schreiben: ohne Anrede-Links, ohne Tags */
export function paragraphsFromMailHtml(html: string): string[] {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .split(/<\/p>/i)
    .map((p) => p.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, "\"").replace(/&#39;/g, "'").trim())
    .filter((p) => p.length > 0 && !/^https?:\/\//.test(p));
}

/** Bezeichnung der Gebührenzeile: Verzugspauschale mit Rechtsgrundlage bzw. Hinweis auf die Anrechnung (Prüfung M-1). */
export function feeLabel(basis: DunningCosts["feeBasis"], lang: "de" | "en", fallback: string): string {
  if (!basis) return fallback;
  const law = basis.law?.toUpperCase();
  // K-322: Schweiz/Liechtenstein kennen keine gesetzliche Pauschale; eine (vertraglich vereinbarte) Gebühr heisst Mahngebühr
  if (law === "CH" || law === "LI") return lang === "en" ? "Dunning fee" : "Mahngebühr";
  if (basis.kind === "flat") {
    const ref = law === "DE" ? (lang === "en" ? " (section 288 (5) BGB)" : " (§ 288 Abs. 5 BGB)") : law === "AT" ? (lang === "en" ? " (section 458 UGB)" : " (§ 458 UGB)") : "";
    // Österreich: § 458 UGB spricht von der Entschädigung für Betreibungskosten (Nachtest 14.10.2026)
    if (law === "AT") return (lang === "en" ? "Flat compensation for recovery costs" : "Pauschale für Betreibungskosten") + ref;
    return (lang === "en" ? "Flat late-payment fee" : "Verzugspauschale") + ref;
  }
  // K-326: Österreich nennt die Pauschale nach § 458 UGB
  if (law === "AT") return lang === "en" ? "Dunning fees (flat compensation for recovery costs credited against them)" : "Mahnspesen (die Pauschale für Betreibungskosten ist darauf angerechnet)";
  return lang === "en" ? "Dunning fees (flat late-payment fee credited against them)" : "Mahngebühren (die Verzugspauschale ist darauf angerechnet)";
}

/**
 * Gruß am Ende eines Absatzes erkennen: „Mit freundlichen Grüßen“, „Freundliche Grüsse“, „Viele Grüße“, „Vielen Dank und
 * freundliche Grüße“ (Zahlungserinnerung), „Kind regards“, „Thank you and kind regards“ … K-407: Vorher erkannte die Prüfung
 * nur Absätze, die mit dem Grußwort beginnen; bei der Zahlungserinnerung („Vielen Dank und …“) stand der Gruß deshalb über der
 * Forderungsaufstellung statt nach der Bankverbindung.
 */
const GRUSS = /^\s*(?:(?:vielen|herzlichen|besten) dank(?:\s+(?:und|sowie))?,?\s+|danke(?:\s+und)?,?\s+|(?:many\s+)?thanks(?:\s+and)?,?\s+|thank you(?:\s+and)?,?\s+)?(?:(?:mit\s+)?(?:freundlichen|freundliche|vielen|viele|besten|beste|herzlichen|herzliche|lieben|liebe|sonnige|kind|best|warm|warmest|kindest)\s+)?(?:grüßen|grüssen|grüße|grüsse|gruß|gruss|regards|wishes)(?![a-zäöüß])|^\s*(?:yours\s+)?(?:sincerely|faithfully)(?![a-z])/i;

/** Trennt den Gruß samt allem danach vom Brieftext. */
export function splitSignoff(paragraphs: readonly string[]): { body: string[]; signoff: string[] } {
  const i = paragraphs.findIndex((p) => {
    const first = p.split("\n")[0] ?? "";
    return first.length <= 80 && GRUSS.test(first);
  });
  return i < 0 ? { body: [...paragraphs], signoff: [] } : { body: paragraphs.slice(0, i), signoff: paragraphs.slice(i) };
}

/**
 * Bezeichnung der Gebühr für den Fließtext der Mahnmail ({{dunning.fee_label}}, K-405): dieselbe Bezeichnung wie in der
 * Forderungsaufstellung (feeLabel), auf Englisch kleingeschrieben, weil sie mitten im Satz steht („plus €40.00 flat
 * late-payment fee (section 288 (5) BGB)“). Gleich in SQL: app.dunning_fee_label (0286).
 */
export function dunningFeeMailLabel(basis: DunningCosts["feeBasis"] | null | undefined, lang: "de" | "en"): string {
  const label = feeLabel(basis ?? undefined, lang, lang === "en" ? "Dunning fee" : "Mahngebühr");
  return lang === "en" ? label.charAt(0).toLowerCase() + label.slice(1) : label;
}
